import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Tag, ChevronRight, Filter, ExternalLink } from 'lucide-react';
import SEO, { breadcrumbSchema } from '../components/SEO';
import SEOContentBlock from '../components/SEOContentBlock';
import { useLanguage } from '../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_COLORS = {
  'Technical': '#E8520A',
  'Expeditions': '#2563eb',
  'News': '#16a34a',
  'Build Guides': '#9333ea',
  'Customer Stories': '#dc2626',
};

export default function Articles() {
  const { language } = useLanguage();
  const fr = language === 'fr';

  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [activeCategory, setActiveCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [feedArticles, setFeedArticles] = useState([]);

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/api/articles?page=${page}&limit=12`;
      if (activeCategory) url += `&category=${encodeURIComponent(activeCategory)}`;
      const res = await fetch(url);
      const data = await res.json();
      setArticles(data.articles || []);
      setCategories(data.categories || []);
      setTags(data.tags || []);
      setTotalPages(data.pages || 1);
    } catch (err) {
      console.error('Failed to fetch articles:', err);
    }
    setLoading(false);
  }, [page, activeCategory]);

  useEffect(() => { fetchArticles(); }, [fetchArticles]);

  useEffect(() => {
    fetch(`${API}/api/external-feed`)
      .then(r => r.json())
      .then(d => setFeedArticles(d.articles || []))
      .catch(() => {});
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString(fr ? 'fr-CA' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="min-h-screen pt-[76px]" style={{ backgroundColor: '#000' }}>
      <SEO
        title={fr ? 'Articles' : 'Articles & Insights'}
        description="Technical articles, expedition stories, build guides, and industry news from Total Composites — the leader in composite truck camper shells and expedition vehicle technology."
        path="/articles"
        keywords="truck camper articles, expedition camper blog, composite camper guides, overlanding articles, Total Composites news, truck camper build guide"
        jsonLd={breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Articles', path: '/articles' }
        ])}
      />

      {/* Hero Section — black, matches Media */}
      <section className="bg-black py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#E8520A', letterSpacing: '0.15em' }}>
            {fr ? 'Blogue Total Composites' : 'Total Composites Blog'}
          </p>
          <h1 className="text-4xl lg:text-5xl font-black text-white mb-4 uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>
            {fr ? 'NOUVELLES & PERSPECTIVES' : 'NEWS & INSIGHTS'}
          </h1>
          <p className="text-gray-400 max-w-2xl leading-relaxed text-sm">
            {fr
              ? 'Nouvelles de l\'industrie, mises à jour produits et articles techniques — publiés par l\'équipe Total Composites.'
              : 'Industry news, product updates, and technical articles — published by the Total Composites team.'}
          </p>
        </div>
      </section>

      {/* Articles Content — white section, matches Media */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section heading with line */}
          <div className="flex items-center gap-4 mb-8">
            <h2 className="text-2xl font-black text-gray-900 whitespace-nowrap" style={{ fontFamily: 'Inter, sans-serif' }}>
              {fr ? 'Derniers articles' : 'Latest Articles'}
            </h2>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* Category Filters */}
          {categories.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-10">
              <Filter size={14} className="text-gray-400 mr-1" />
              <button
                onClick={() => { setActiveCategory(''); setPage(1); }}
                className={`px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-full border transition-all ${
                  !activeCategory
                    ? 'text-gray-900 border-gray-900'
                    : 'text-gray-400 border-gray-200 hover:border-gray-400'
                }`}
                data-testid="filter-all"
              >
                {fr ? 'Tout' : 'All'}
              </button>
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => { setActiveCategory(cat); setPage(1); }}
                  className={`px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-full border transition-all ${
                    activeCategory === cat
                      ? 'text-white'
                      : 'text-gray-400 border-gray-200 hover:border-gray-400'
                  }`}
                  style={activeCategory === cat ? { borderColor: CATEGORY_COLORS[cat] || '#E8520A', backgroundColor: CATEGORY_COLORS[cat] || '#E8520A' } : {}}
                  data-testid={`filter-${cat.toLowerCase()}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}

          {/* Grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="bg-white border border-gray-100 rounded-xl overflow-hidden animate-pulse shadow-sm">
                  <div className="h-48 bg-gray-100" />
                  <div className="p-5 space-y-3">
                    <div className="h-3 bg-gray-100 rounded w-1/3" />
                    <div className="h-5 bg-gray-100 rounded w-3/4" />
                    <div className="h-3 bg-gray-100 rounded w-full" />
                    <div className="h-3 bg-gray-100 rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-20" data-testid="articles-empty">
              <div className="text-5xl mb-4 opacity-20">&#9998;</div>
              <h2 className="text-lg font-bold text-gray-900 mb-2">
                {fr ? 'Aucun article pour le moment' : 'No Articles Yet'}
              </h2>
              <p className="text-sm text-gray-500 max-w-md mx-auto">
                {fr
                  ? 'Les articles seront publiés prochainement. Revenez bientôt pour des guides techniques, des récits d\'expédition et des nouvelles.'
                  : 'Articles are coming soon. Check back for technical guides, expedition stories, and industry news from Total Composites.'}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="articles-grid">
                {articles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/articles/${article.slug}`}
                    className="group flex flex-col border border-gray-100 rounded-xl overflow-hidden shadow-sm hover:shadow-lg hover:border-orange-200 transition-all duration-300 bg-white"
                    style={{ textDecoration: 'none' }}
                    data-testid={`article-card-${article.slug}`}
                  >
                    {/* Featured Image */}
                    <div className="relative overflow-hidden" style={{ height: 180 }}>
                      {article.featured_image ? (
                        <img
                          src={article.featured_image}
                          alt={article.title}
                          className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-50">
                          <span className="text-4xl font-black text-gray-200">{article.title?.[0] || 'A'}</span>
                        </div>
                      )}
                      {/* Category Badge */}
                      {article.categories?.[0] && (
                        <span
                          className="absolute top-3 left-3 px-2.5 py-1 text-xs font-black uppercase rounded"
                          style={{ backgroundColor: CATEGORY_COLORS[article.categories[0]] || '#E8520A', color: '#fff', fontSize: '0.58rem', letterSpacing: '0.08em' }}
                        >
                          {article.categories[0]}
                        </span>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex flex-col flex-1 border-t border-gray-100">
                      <div className="flex flex-col gap-2 p-5 pb-4" style={{ backgroundColor: '#f8f8f8', borderBottom: '1px solid #efefef' }}>
                        <div className="flex items-center gap-3 text-xs text-gray-400 font-medium uppercase tracking-widest">
                          <span>{formatDate(article.publish_date)}</span>
                          <span className="flex items-center gap-1">
                            <Clock size={10} />
                            {article.read_time} min
                          </span>
                        </div>
                        <p className="text-xs font-black text-gray-900 uppercase leading-snug" style={{ letterSpacing: '0.04em' }}>
                          {article.title}
                        </p>
                      </div>
                      <div className="flex flex-col gap-2.5 p-5 flex-1">
                        <p className="text-xs text-gray-500 leading-relaxed flex-1 line-clamp-3">
                          {article.excerpt}
                        </p>
                        {/* Tags + Read More */}
                        <div className="border-t border-gray-100 pt-3 mt-1">
                          {article.tags?.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-2">
                              {article.tags.slice(0, 3).map(tag => (
                                <span key={tag} className="flex items-center gap-0.5 text-xs text-gray-400">
                                  <Tag size={8} />{tag}
                                </span>
                              ))}
                            </div>
                          )}
                          <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest" style={{ color: '#E8520A', fontSize: '0.62rem' }}>
                            <ChevronRight size={10} /> {fr ? 'Lire l\'article' : 'Read Article'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-12">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                    <button
                      key={p}
                      onClick={() => { setPage(p); window.scrollTo(0, 0); }}
                      className={`w-8 h-8 text-xs font-bold rounded ${
                        p === page ? 'text-white' : 'text-gray-500 hover:text-gray-900'
                      }`}
                      style={p === page ? { backgroundColor: '#E8520A' } : {}}
                      data-testid={`page-${p}`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* External Blog Feed — Expedition Upfitter */}
      {feedArticles.length > 0 && (
        <section className="bg-white py-16 border-t border-gray-100" data-testid="partner-feed">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between gap-4 mb-8">
              <div className="flex items-center gap-4 min-w-0">
                <h2 className="text-2xl font-black text-gray-900 whitespace-nowrap" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {fr ? 'Dernières de Expedition Upfitter' : 'Latest from Expedition Upfitter'}
                </h2>
                <div className="flex-1 h-px bg-gray-200 hidden sm:block" />
              </div>
              <a
                href="https://www.expeditionupfitter.ca/blogs/news"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest shrink-0 transition-colors hover:opacity-75"
                style={{ color: '#E8520A' }}
                data-testid="feed-view-all"
              >
                {fr ? 'Voir tout' : 'View All'} <ExternalLink size={10} />
              </a>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {feedArticles.map((item, idx) => (
                <a
                  key={item.url || item.title}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex flex-col border border-gray-100 rounded-xl overflow-hidden shadow-sm hover:shadow-lg hover:border-orange-200 transition-all duration-300 bg-white"
                  style={{ textDecoration: 'none' }}
                  data-testid={`feed-card-${idx}`}
                >
                  {/* Image */}
                  <div className="relative overflow-hidden" style={{ height: 180 }}>
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-50">
                        <span className="text-4xl font-black text-gray-200">{item.title?.[0] || 'E'}</span>
                      </div>
                    )}
                    {/* External badge */}
                    <span className="absolute top-3 left-3 px-2.5 py-1 text-white bg-black/60 backdrop-blur-sm rounded" style={{ fontSize: '0.55rem', letterSpacing: '0.08em', fontWeight: 800, textTransform: 'uppercase' }}>
                      Expedition Upfitter
                    </span>
                  </div>

                  {/* Content */}
                  <div className="flex flex-col flex-1 border-t border-gray-100">
                    <div className="flex flex-col gap-2 p-5 pb-4" style={{ backgroundColor: '#f8f8f8', borderBottom: '1px solid #efefef' }}>
                      <div className="flex items-center gap-3 text-xs text-gray-400 font-medium uppercase tracking-widest">
                        {item.published && <span>{formatDate(item.published)}</span>}
                      </div>
                      <p className="text-xs font-black text-gray-900 uppercase leading-snug" style={{ letterSpacing: '0.04em' }}>
                        {item.title}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2.5 p-5 flex-1">
                      <p className="text-xs text-gray-500 leading-relaxed flex-1 line-clamp-3">
                        {item.excerpt}
                      </p>
                      <div className="border-t border-gray-100 pt-3 mt-1">
                        <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest" style={{ color: '#E8520A', fontSize: '0.62rem' }}>
                          <ExternalLink size={10} /> {fr ? 'Lire sur Expedition Upfitter' : 'Read on Expedition Upfitter'}
                        </div>
                      </div>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA — after feed */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-black text-gray-900 mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
            {fr ? 'Restez informé' : 'Stay Informed'}
          </h2>
          <p className="text-gray-500 mb-10 text-sm leading-relaxed">
            {fr
              ? 'Articles techniques, récits d\'expédition et guides de construction — directement des experts en campers composites.'
              : 'Technical articles, expedition stories, and build guides — straight from the composite truck camper experts.'}
          </p>
          <a href="/contact"
            className="inline-flex items-center gap-2 px-7 py-3.5 font-bold text-white rounded-full text-sm transition-all duration-200 hover:opacity-90 hover:scale-105"
            style={{ backgroundColor: '#E8520A' }}
            data-testid="articles-cta"
          >
            {fr ? 'CONTACTEZ-NOUS' : 'CONTACT US'} <ChevronRight size={14} />
          </a>
        </div>
      </section>

      {/* SEO Content Block */}
      <SEOContentBlock title="About Total Composites Articles">
        <p>Stay informed with the latest from Total Composites — technical articles covering composite truck camper construction, FRP pultrusion technology, and thermal bridging elimination. Read real expedition stories from owners who've taken their Lynx, Wolf, and Wolverine campers to the ends of the earth, from the Canadian Arctic to the Australian Outback.</p>
        <p>Our build guides provide step-by-step insight into how authorized builder/partners transform empty composite shells into fully appointed expedition vehicles. Whether you're researching your first truck camper or you're a seasoned overlander looking for the latest in composite camper technology, our articles deliver the depth and expertise you need.</p>
      </SEOContentBlock>
    </div>
  );
}
